import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { FiCheck } from 'react-icons/fi';
import { submitLead } from '../lib/submissions';

const ticketTiers = [
  {
    name: 'Regular Guest',
    price: 999,
    features: [
      'Entry to all rituals',
      'Traditional Sadya feast',
      'Common photo zones access',
      'Chenda Melam performance',
      'Welcome drink'
    ],
    recommended: false
  },
  {
    name: 'Premium Guest',
    price: 2999,
    features: [
      'Everything in Regular',
      'Better seating arrangement',
      'Priority Sadya service',
      'Professional photo access',
      'Return gift hamper',
      'Reserved parking'
    ],
    recommended: true
  },
  {
    name: 'VIP Experience',
    price: 9999,
    features: [
      'Everything in Premium',
      'Best seating at front',
      'Meet-the-couple photo op',
      'Backstage access',
      'Premium gift box',
      'Personal event coordinator',
      'Special mention'
    ],
    recommended: false
  },
  {
    name: 'Family Table',
    price: 15000,
    features: [
      'Reserved table for 6 people',
      'Family group photo',
      'Family intro on stage',
      'All Premium benefits',
      'Customized menu options',
      'Dedicated service staff'
    ],
    recommended: false
  }
];

const TicketSection = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: 1,
    eventDate: 'March 15, 2025'
  });

  const handleBookNow = (ticket) => {
    setSelectedTicket(ticket);
    setIsBookingOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmitLead = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      await submitLead({
        formType: 'ticket',
        ticketType: selectedTicket.name,
        quantity: formData.quantity,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        eventDate: formData.eventDate,
        totalAmount: selectedTicket.price * formData.quantity,
        status: 'pending',
        inquiryType: 'ticket'
      });
      
      toast.success('Thank you! We\'ll contact you shortly to complete the booking.');
      setIsBookingOpen(false);
      setFormData({ name: '', email: '', phone: '', quantity: 1, eventDate: 'March 15, 2025' });
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Failed to submit. Please try again or contact us on WhatsApp.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <section id="tickets" className="py-24 md:py-32 px-6 bg-stone-50" data-testid="ticket-section">
      <Toaster position="top-center" richColors />
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-normal text-emerald-900 mb-4" data-testid="ticket-title">
            Choose Your Experience
          </h2>
          <p className="text-lg text-stone-600" data-testid="ticket-subtitle">
            Select the perfect ticket tier for your Kerala wedding adventure
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {ticketTiers.map((ticket, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`glass-card p-8 rounded-2xl relative ${
                ticket.recommended ? 'border-2 border-yellow-600 shadow-xl' : ''
              }`}
              data-testid={`ticket-card-${index}`}
            >
              {ticket.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-600 text-white px-6 py-2 rounded-full text-sm font-medium" data-testid="recommended-badge">
                  Recommended
                </div>
              )}
              
              <h3 className="text-2xl font-medium text-emerald-900 mb-2">
                {ticket.name}
              </h3>
              <div className="mb-6">
                <span className="text-4xl font-bold text-yellow-600">₹{ticket.price.toLocaleString()}</span>
                <span className="text-stone-600 text-sm ml-2">per person</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {ticket.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-stone-700">
                    <FiCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => handleBookNow(ticket)}
                className={`w-full rounded-full py-6 font-medium transition-all duration-300 ${
                  ticket.recommended
                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    : 'bg-emerald-900 hover:bg-emerald-800 text-white'
                }`}
                data-testid={`book-now-btn-${index}`}
              >
                Book Now
              </Button>
            </motion.div>
          ))}
        </div>
      </div>

      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="sm:max-w-md" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-emerald-900" data-testid="booking-dialog-title">
              Book {selectedTicket?.name}
            </DialogTitle>
            <DialogDescription data-testid="booking-dialog-description">
              ₹{selectedTicket?.price.toLocaleString()} per ticket | Event: March 15, 2025
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                data-testid="booking-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                data-testid="booking-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+91 XXXXX XXXXX"
                data-testid="booking-phone-input"
              />
            </div>
            
            <div>
              <Label htmlFor="quantity">Number of Tickets</Label>
              <Input
                id="quantity"
                name="quantity"
                type="number"
                min="1"
                max="10"
                value={formData.quantity}
                onChange={handleInputChange}
                data-testid="booking-quantity-input"
              />
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-medium">Estimated Total:</span>
                <span className="text-2xl font-bold text-yellow-600">
                  ₹{(selectedTicket?.price * formData.quantity).toLocaleString()}
                </span>
              </div>
              
              <Button
                onClick={handleSubmitLead}
                disabled={isProcessing}
                className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white py-6 font-medium"
                data-testid="proceed-booking-btn"
              >
                {isProcessing ? 'Submitting...' : 'Submit Booking Request'}
              </Button>
              <p className="text-xs text-center text-stone-500 mt-2">
                We'll contact you to confirm and complete the booking
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default TicketSection;
